<template>
  <div class="app-layout" :class="layoutClass">
    <!-- 侧边栏 -->
    <Sidebar 
      v-model:collapsed="sidebarCollapsed"
      :menu-data="filteredMenuData"
      @menu-select="handleMenuSelect"
    />
    
    <!-- 主内容区 -->
    <div class="main-container">
      <!-- 顶部栏 -->
      <Header 
        :user-info="userInfo"
        :collapsed="sidebarCollapsed"
        @toggle-sidebar="toggleSidebar"
        @user-action="handleUserAction"
      />
      
      <!-- 内容区域 -->
      <div class="content-wrapper">
        <!-- 面包屑导航 -->
        <Breadcrumb :routes="breadcrumbRoutes" />
        
        <!-- 页面内容 -->
        <div class="page-content">
          <!-- 页面加载状态 -->
          <div v-if="pageLoading" class="page-loading">
            <el-icon class="is-loading">
              <Loading />
            </el-icon>
            <span>页面加载中...</span>
          </div>
          
          <!-- 页面内容 -->
          <router-view v-else v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <keep-alive :include="cachedViews">
                <Suspense>
                  <template #default>
                    <component :is="Component" />
                  </template>
                  <template #fallback>
                    <div class="page-loading">
                      <el-icon class="is-loading">
                        <Loading />
                      </el-icon>
                      <span>页面加载中...</span>
                    </div>
                  </template>
                </Suspense>
              </keep-alive>
            </transition>
          </router-view>
        </div>
      </div>
      

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { Loading } from '@element-plus/icons-vue'
import Sidebar from './Sidebar.vue'
import Header from './Header.vue'
import Breadcrumb from './Breadcrumb.vue'
import { filterMenuByPermissions, setUserPermissions, clearUserPermissions, getUserPermissions } from '@/utils/permission'
import apiClient from '@/utils/request'

const router = useRouter()

// 获取当前用户信息
const getCurrentUser = () => {
  // 从localStorage获取用户信息
  const userInfo = localStorage.getItem('userInfo')
  if (userInfo) {
    try {
      const user = JSON.parse(userInfo)
      return {
        id: user.staffId || user.id || user.userId || '',
        name: user.realName || user.username || user.staffName || 'admin'
      }
    } catch (e) {
      console.warn('解析用户信息失败:', e)
    }
  }
  // 从token中获取用户信息
  const token = localStorage.getItem('token')
  if (token) {
    try {
      // 简单解析JWT token
      const payload = JSON.parse(atob(token.split('.')[1]))
      return {
        id: payload.staffId || payload.sub || payload.userId || '',
        name: payload.realName || payload.username || payload.name || 'admin'
      }
    } catch (e) {
      console.warn('解析token失败:', e)
    }
  }
  // 默认返回空值
  return {
    id: '',
    name: 'admin'
  }
}

// 响应式数据
const sidebarCollapsed = ref(false)
const isMobile = ref(false)
const permissionsLoaded = ref(false) // 添加权限加载状态
const pageLoading = ref(false) // 添加页面加载状态

// 用户信息
const userInfo = ref({
  name: '管理员',
  avatar: '',
  role: 'admin'
})

// 从localStorage获取用户信息
const loadUserInfo = async () => {
  try {
    console.log('🔍 开始加载localStorage用户信息...')
    const storedUserInfo = localStorage.getItem('userInfo')
    if (storedUserInfo) {
      const user = JSON.parse(storedUserInfo)
      userInfo.value = {
        name: user.username || '管理员',
        avatar: user.avatar || '',
        role: user.role || 'admin'
      }
      
      // 如果有权限信息，设置用户权限
      if (user.permissions && Array.isArray(user.permissions)) {
        setUserPermissions(user.permissions)
        permissionsLoaded.value = true // 标记权限已加载
        console.log('✅ 从localStorage加载权限成功:', user.permissions.length, '个权限')
        console.log('权限列表:', user.permissions.slice(0, 10), '...')
        
        // 同时设置到权限工具函数中
        const { setUserPermissions: setPermissionUtils } = await import('@/utils/permission')
        setPermissionUtils(user.permissions)
        console.log('✅ 权限工具函数设置成功')
      } else {
        console.warn('⚠️ localStorage中没有权限信息或格式不正确')
      }
    } else {
      console.warn('⚠️ localStorage中没有用户信息')
    }
  } catch (error) {
    console.error('❌ 解析用户信息失败:', error)
  }
}

// 加载用户权限
const loadUserPermissions = async () => {
  try {
    // 如果localStorage中已有权限，跳过服务器加载
    if (permissionsLoaded.value) {
      console.log('✅ 权限已从localStorage加载，跳过服务器加载')
      return
    }
    
    const userId = getCurrentUser().id
    if (!userId) {
      console.warn('⚠️ 用户ID为空，无法加载权限')
      return
    }

    console.log('🔄 开始从服务器加载用户权限，用户ID:', userId)
    
    const response = await apiClient.get(`/permissions/user/${userId}`) as any
    
    if (response.code === 200 && response.data) {
      setUserPermissions(response.data)
      permissionsLoaded.value = true // 标记权限已加载
      console.log('✅ 从服务器加载权限成功:', response.data.length, '个权限')
      console.log('✅ 权限详情:', response.data)
    } else {
      console.warn('❌ 从服务器加载权限失败:', response.message)
      // 如果服务器加载失败，尝试从localStorage加载
      const storedUserInfo = localStorage.getItem('userInfo')
      if (storedUserInfo) {
        const user = JSON.parse(storedUserInfo)
        if (user.permissions && Array.isArray(user.permissions)) {
          setUserPermissions(user.permissions)
          permissionsLoaded.value = true
          console.log('✅ 从localStorage加载权限成功:', user.permissions.length, '个权限')
        }
      }
    }
  } catch (error) {
    console.error('❌ 加载用户权限异常:', error)
    // 如果服务器加载失败，尝试从localStorage加载
    const storedUserInfo = localStorage.getItem('userInfo')
    if (storedUserInfo) {
      const user = JSON.parse(storedUserInfo)
      if (user.permissions && Array.isArray(user.permissions)) {
        setUserPermissions(user.permissions)
        permissionsLoaded.value = true
        console.log('✅ 从localStorage加载权限成功:', user.permissions.length, '个权限')
      }
    }
  }
}

// 根据权限过滤的菜单数据
const filteredMenuData = computed(() => {
  // 只有在权限加载完成后才进行过滤
  if (!permissionsLoaded.value) {
    console.log('⏳ [AppLayout] 权限未加载完成，返回原始菜单')
    return menuData.value
  }
  
  const currentPerms = getUserPermissions()
  console.log('🔍 [AppLayout] 开始过滤菜单，当前权限数量:', currentPerms.length)
  console.log('🔍 [AppLayout] 权限列表前20项:', currentPerms.slice(0, 20))
  console.log('🔍 [AppLayout] 原始菜单数据:', menuData.value)
  
  const filtered = filterMenuByPermissions(menuData.value)
  console.log('✅ [AppLayout] 菜单过滤完成，过滤后菜单数量:', filtered.length)
  console.log('✅ [AppLayout] 过滤后的菜单:', filtered)
  
  // 检查会话存档菜单是否在过滤后的菜单中
  const systemMenu = filtered.find((m: any) => m.path === '/system')
  if (systemMenu && systemMenu.children) {
    const archiveMenu = systemMenu.children.find((m: any) => m.path === '/system/archive')
    console.log('🔍 [AppLayout] 系统管理菜单:', systemMenu)
    console.log('🔍 [AppLayout] 系统管理子菜单数量:', systemMenu.children.length)
    console.log('🔍 [AppLayout] 系统管理子菜单列表:', systemMenu.children.map((m: any) => m.meta?.title))
    console.log('🔍 [AppLayout] 会话存档菜单:', archiveMenu)
    if (archiveMenu) {
      console.log('✅ [AppLayout] 会话存档菜单存在，子菜单数量:', archiveMenu.children?.length || 0)
    } else {
      console.log('❌ [AppLayout] 会话存档菜单不存在！')
      console.log('🔍 [AppLayout] 检查原始菜单数据中的会话存档:', menuData.value.find((m: any) => m.path === '/system')?.children?.find((m: any) => m.path === '/system/archive'))
    }
  } else {
    console.log('❌ [AppLayout] 系统管理菜单不存在或没有子菜单')
  }
  
  return filtered
})

const menuData = ref([
  {
    path: '/dashboard',
    meta: { title: '工作台', icon: 'Dashboard' }
  },

  {
    path: '/basedata',
    meta: { title: '基础数据', icon: 'DataBoard' },
    children: [
      {
        path: '/stores',
        meta: { title: '门店管理', icon: 'Shop', permission: 'MOD0021' }
      },
      {
        path: '/warehouses',
        meta: { title: '仓库管理', icon: 'Box', permission: 'MOD0022' }
      },
      {
        path: '/kitchens',
        meta: { title: '后厨管理', icon: 'House', permission: 'MOD0023' }
      },
      {
        path: '/material-categories',
        meta: { title: '材料分类', icon: 'Goods', permission: 'MOD0026' }
      },
      {
        path: '/materials',
        meta: { title: '材料管理', icon: 'Goods', permission: 'MOD0024' }
      },
      {
        path: '/product-categories',
        meta: { title: '产品分类', icon: 'Goods', permission: 'MOD0025' }
      },
      {
        path: '/recipes',
        meta: { title: '配方管理', icon: 'Document', permission: 'MOD0027' }
      },
      {
        path: '/product-skus',
        meta: { title: '产品SKU', icon: 'Goods', permission: 'MOD0028' }
      },
      {
        path: '/product-boms',
        meta: { title: '产品BOM', icon: 'List', permission: 'MOD0029' }
      },
      {
        path: '/suppliers',
        meta: { title: '供应商管理', icon: 'OfficeBuilding', permission: 'MOD002A' }
      },
      {
        path: '/inventory/loss-reason',
        meta: { title: '货损原因', icon: 'Warning', permission: 'MOD002B' }
      }
    ]
  },
  {
    path: '/inventory',
    meta: { title: '库存管理', icon: 'Box' },
    children: [
      {
        path: '/inventory/purchase-record',
        meta: { title: '材料采购记录', icon: 'ShoppingCart', permission: 'MOD0031' }
      },
      {
        path: '/inventory/material',
        meta: { title: '材料库存管理', icon: 'Box', permission: 'MOD0032' }
      },
      {
        path: '/inventory/product',
        meta: { title: '产品库存管理', icon: 'Box', permission: 'MOD0033' }
      },
      {
        path: '/inventory/transactions',
        meta: { title: '库存流水记录', icon: 'List', permission: 'MOD0034' }
      },
      {
        path: '/inventory/adjustments',
        meta: { title: '库存调整管理', icon: 'Edit', permission: 'MOD0035' }
      }
    ]
  },
  {
    path: '/production',
    meta: { title: '产销管理', icon: 'Edit' },
    children: [
      {
        path: '/production/records',
        meta: { title: '生产记录管理', icon: 'Document', permission: 'MOD0041' }
      },
      {
        path: '/sales-record',
        meta: { title: '销售记录管理', icon: 'Document', permission: 'MOD0042' }
      },
      {
        path: '/custom-cake-order',
        meta: { title: '定制蛋糕订单', icon: 'Document', permission: 'MOD0044' }
      },
      {
        path: '/inventory/loss-records',
        meta: { title: '报损记录管理', icon: 'Warning', permission: 'MOD0043' }
      }
    ]
  },
  {
    path: '/reports',
    meta: { title: '统计报表', icon: 'DataLine' },
    children: [
      {
        path: '/inventory/overview',
        meta: { title: '库存概览', icon: 'DataLine', permission: 'MOD0051' }
      },
      {
        path: '/reports/daily-reconciliation',
        meta: { title: '日对账报表', icon: 'Document', permission: 'MOD0052' }
      },
      {
        path: '/reports/personal-daily-reconciliation',
        meta: { title: '个人日对账', icon: 'User', permission: 'MOD0053' }
      },
      {
        path: '/reports/historical-summary',
        meta: { title: '历史汇总数据', icon: 'DataAnalysis', permission: 'MOD0054' }
      }
    ]
  },
  {
    path: '/system',
    meta: { title: '系统管理', icon: 'Setting' },
    children: [
      {
        path: '/system/code-rules',
        meta: { title: '编码规则', icon: 'Key', permission: 'MOD0061' }
      },
      {
        path: '/system/staff',
        meta: { title: '人员管理', icon: 'User', permission: 'MOD0062' }
      },
      {
        path: '/system/roles',
        meta: { title: '角色管理', icon: 'UserFilled', permission: 'MOD0063' }
      },
      {
        path: '/system/permissions',
        meta: { title: '权限管理', icon: 'Lock', permission: 'MOD0064' }
      },
      {
        path: '/system/modules',
        meta: { title: '模块管理', icon: 'Folder', permission: 'MOD0065' }
      },
      {
        path: '/system/backup',
        meta: { title: '系统备份', icon: 'Download', permission: 'MOD0066' }
      },
      {
        path: '/system/llm-config',
        meta: { title: '大模型配置', icon: 'Cpu', permission: 'MOD0067' }
      },
      {
        path: '/system/integration',
        meta: { title: '系统对接', icon: 'Connection', permission: 'MOD0068' }
      },
      {
        path: '/system/archive',
        meta: { title: '会话存档', icon: 'ChatLineRound', permission: 'MOD0069' }
        // 子路由不在菜单中显示，通过父路由的MOD0069权限统一控制
      }
    ]
  }
])

const breadcrumbRoutes = ref([])
const cachedViews = ref(['Dashboard', 'BaseData'])

// 计算属性
const layoutClass = computed(() => ({
  'layout-mobile': isMobile.value,
  'layout-collapsed': sidebarCollapsed.value
}))

// 方法
const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

const handleMenuSelect = (menuItem: any) => {
  console.log('菜单选择:', menuItem)
  if (menuItem.path) {
    pageLoading.value = true
    router.push(menuItem.path).finally(() => {
      // 延迟一点时间确保页面完全加载
      setTimeout(() => {
        pageLoading.value = false
      }, 300)
    })
  }
}

const handleUserAction = (action: string) => {
  console.log('用户操作:', action)
}

// 检查是否为移动端
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}

onMounted(async () => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  
  // 1. 先加载localStorage中的用户信息和权限
  loadUserInfo()
  
  // 2. 等待一下确保localStorage权限已设置
  await nextTick()
  
  // 3. 再异步加载服务器权限（如果有的话）
  await loadUserPermissions()
  
  // 4. 强制刷新权限（确保权限已正确加载）
  await nextTick()
  const { refreshPermissions, getUserPermissions } = await import('@/utils/permission')
  refreshPermissions()
  const perms = getUserPermissions()
  console.log('✅ 权限刷新完成，当前权限数量:', perms.length)
  console.log('✅ 会话存档相关权限:', perms.filter((p: string) => p.includes('MOD0069')))
})
</script>

<style lang="scss" scoped>
.app-layout {
  height: 100vh;
  display: flex;
  
  .main-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-left: 240px;
    transition: margin-left 0.3s ease;
    overflow: hidden;
    
    .content-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      background-color: #f5f5f5;
      overflow: hidden;
      margin: 0;
      padding: 0;
      
      .page-content {
        flex: 1;
        margin: 0;
        background: white;
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
      }
    }
  }
  
  &.layout-collapsed .main-container {
    margin-left: 64px;
  }
  
  &.layout-mobile .main-container {
    margin-left: 0;
  }
}

/* 页面加载状态样式 */
.page-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #666;
  font-size: 14px;
  
  .el-icon {
    font-size: 24px;
    margin-bottom: 10px;
  }
}
</style> 